// ============================================================================
// Preferencias de apariencia — persistidas en localStorage, por navegador.
// Store mínimo con useSyncExternalStore: sin dependencias nuevas.
// ============================================================================

import { useSyncExternalStore } from "react";

export type ThemeColor = "teal" | "blue" | "purple" | "pink" | "green" | "orange" | "red";
export type FontSize = "small" | "medium" | "large";

export interface AppearanceState {
  dark: boolean;
  themeColor: ThemeColor;
  fontSize: FontSize;
  compactMode: boolean;
  animationsEnabled: boolean;
  sidebarFixed: boolean;
}

export const DEFAULT_APPEARANCE: AppearanceState = {
  dark: true,
  themeColor: "teal",
  fontSize: "medium",
  compactMode: false,
  animationsEnabled: true,
  sidebarFixed: true,
};

/** Cada color define primary/ring/chart-1 para claro y oscuro. `teal` = look actual. */
export const THEME_COLORS: Record<
  ThemeColor,
  { label: string; swatch: string; light: string; dark: string }
> = {
  teal: {
    label: "Teal LicitaPro",
    swatch: "oklch(0.62 0.16 205)",
    light: "oklch(0.55 0.16 215)",
    dark: "oklch(0.72 0.16 195)",
  },
  blue: {
    label: "Azul",
    swatch: "oklch(0.6 0.18 255)",
    light: "oklch(0.55 0.18 260)",
    dark: "oklch(0.7 0.16 258)",
  },
  purple: {
    label: "Morado",
    swatch: "oklch(0.6 0.2 300)",
    light: "oklch(0.55 0.2 300)",
    dark: "oklch(0.7 0.18 300)",
  },
  pink: {
    label: "Rosa",
    swatch: "oklch(0.65 0.21 350)",
    light: "oklch(0.58 0.21 350)",
    dark: "oklch(0.72 0.18 350)",
  },
  green: {
    label: "Verde",
    swatch: "oklch(0.65 0.17 155)",
    light: "oklch(0.55 0.16 155)",
    dark: "oklch(0.72 0.17 155)",
  },
  orange: {
    label: "Naranja",
    swatch: "oklch(0.7 0.17 55)",
    light: "oklch(0.62 0.17 55)",
    dark: "oklch(0.76 0.16 60)",
  },
  red: {
    label: "Rojo",
    swatch: "oklch(0.62 0.22 25)",
    light: "oklch(0.56 0.21 25)",
    dark: "oklch(0.68 0.2 25)",
  },
};

const FONT_SIZE_PX: Record<FontSize, string> = {
  small: "14px",
  medium: "16px",
  large: "18px",
};

const STORAGE_KEY = "licitapro:appearance";

let state: AppearanceState = { ...DEFAULT_APPEARANCE };
const listeners = new Set<() => void>();

function isBrowser() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function read(): AppearanceState {
  if (!isBrowser()) return { ...DEFAULT_APPEARANCE };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_APPEARANCE };
    const parsed = JSON.parse(raw) as Partial<AppearanceState>;
    return {
      ...DEFAULT_APPEARANCE,
      ...parsed,
      themeColor: THEME_COLORS[parsed.themeColor as ThemeColor]
        ? (parsed.themeColor as ThemeColor)
        : DEFAULT_APPEARANCE.themeColor,
      fontSize: FONT_SIZE_PX[parsed.fontSize as FontSize]
        ? (parsed.fontSize as FontSize)
        : DEFAULT_APPEARANCE.fontSize,
    };
  } catch {
    return { ...DEFAULT_APPEARANCE };
  }
}

/** Aplica el estado al DOM. Idempotente. */
export function applyAppearance(next: AppearanceState = state) {
  if (!isBrowser()) return;
  const root = document.documentElement;

  root.classList.toggle("dark", next.dark);

  const color = THEME_COLORS[next.themeColor] ?? THEME_COLORS.teal;
  const value = next.dark ? color.dark : color.light;
  root.style.setProperty("--primary", value);
  root.style.setProperty("--ring", value);
  root.style.setProperty("--chart-1", value);
  root.style.setProperty("--sidebar-primary", value);
  root.style.setProperty("--sidebar-ring", value);

  root.style.fontSize = FONT_SIZE_PX[next.fontSize];
  root.classList.toggle("compact-mode", next.compactMode);
  root.classList.toggle("reduce-motion", !next.animationsEnabled);
}

/** Llamar una vez al arrancar la app. */
export function initAppearance() {
  state = read();
  applyAppearance(state);
}

export function setAppearance(patch: Partial<AppearanceState>) {
  state = { ...state, ...patch };
  if (isBrowser()) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* almacenamiento no disponible: la preferencia solo dura la sesión */
    }
  }
  applyAppearance(state);
  listeners.forEach((l) => l());
}

export function resetAppearance() {
  setAppearance({ ...DEFAULT_APPEARANCE });
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return state;
}

export function useAppearance(): AppearanceState {
  return useSyncExternalStore(subscribe, getSnapshot, () => state);
}
